import { darken } from '@mui/material';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { koKR } from '@mui/material/locale';
import { red } from '@mui/material/colors';

const fuseDark = {
  light: '#272f3d',
  main: '#1f2125',
  dark: '#121212',
  contrastText: '#c7c7c8',
};

const skyBlue = {
  100: 'rgba(34,211,238,0.5)',
  500: '#22d3ee',
  900: 'rgb(12, 183, 226)',
};

const lightText = {
  primary: 'rgb(17, 24, 39)',
  secondary: 'rgb(107, 114, 128)',
  disabled: 'rgb(149, 156, 169)',
};

const darkText = {
  primary: 'rgb(255,255,255)',
  secondary: 'rgb(229, 231, 235)',
  disabled: 'rgb(156, 163, 175)',
};

const light = {
  koKR,
  palette: {
    mode: 'light',
    text: lightText,
    common: {
      black: 'rgb(17, 24, 39)',
      white: 'rgb(255, 255, 255)',
    },
    primary: fuseDark,
    secondary: {
      light: skyBlue[100],
      main: skyBlue[500],
      dark: skyBlue[900],
    },
    background: {
      paper: '#FFFFFF',
      default: '#f6f7f9',
    },
    error: red,
  },
  status: {
    danger: 'orange',
  },
  typography: {
    fontFamily:
      '"Noto Sans KR", "Comfortaa", "-apple-system", "sans-serif", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    fontWeightLight: 300,
    fontWeightRegular: 500,
    fontWeightMedium: 700,
    fontWeightBold: 800,
    fontSize: 12,
    h1: {
      fontWeight: 'bold',
    },
    h2: {
      fontWeight: 'bold',
    },
    h3: {
      fontWeight: 'bold',
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      fontWeight: 'bold',
    },
    h6: {
      fontWeight: 'bold',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '3px',
            margin: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.12)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: `${darken('rgba(0,0,0,.12)', 0.16)}`,
            borderRadius: '3px',
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '3px',
            margin: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.12)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: `${darken('rgba(0,0,0,.12)', 0.16)}`,
            borderRadius: '3px',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '3px',
            margin: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.12)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: `${darken('rgba(0,0,0,.12)', 0.16)}`,
            borderRadius: '3px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        // Name of the slot
        root: {
          transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0,
          color: 'rgba(0, 0, 0, 0.87)',
          backgroundImage: 'none',
          backgroundColor: 'rgb(255, 255, 255)',
          boxShadow:
            '0 0 0 0 rgb(0, 0, 0), 0 0 0 0 rgb(0, 0, 0), 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            boxShadow: 'none',
          },
          boxShadow: 'none',
        },
        containedPrimary: {
          color: 'rgba(0, 0, 0, 0.87)',
          backgroundColor: 'rgb(246, 247, 249)',
          '&:hover': {
            backgroundColor: 'rgba(246, 247, 249, .8)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          // padding: '12.5px',
          lineHeight: 1.75,
          '&::-webkit-calendar-picker-indicator': {
            filter: 'invert(1)',
          },
          '&.MuiSelect-select': {
            paddingTop: '10px',
            paddingBottom: '10px',
          },
        },
        notchedOutline: {
          borderColor: 'rgba(0,0,0,.12)',
        },
      },
    },
  },
};

const dark = {
  koKR,
  palette: {
    mode: 'dark',
    text: darkText,
    primary: fuseDark,
    secondary: {
      light: skyBlue[100],
      main: skyBlue[500],
      dark: skyBlue[900],
    },
    background: {
      paper: '#1E2125',
      default: '#121212',
    },
    error: red,
  },
  status: {
    danger: 'orange',
  },
  typography: {
    fontFamily:
      '"Noto Sans KR", "Comfortaa", "-apple-system", "sans-serif", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    fontWeightLight: 300,
    fontWeightRegular: 500,
    fontWeightMedium: 700,
    fontWeightBold: 800,
    fontSize: 12,
    h1: {
      fontWeight: 'bold',
    },
    h2: {
      fontWeight: 'bold',
    },
    h3: {
      fontWeight: 'bold',
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      fontWeight: 'bold',
    },
    h6: {
      fontWeight: 'bold',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '3px',
            margin: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: `${darken('rgba(255, 255, 255, 0.12)', 0.16)}`,
            borderRadius: '3px',
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '3px',
            margin: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: `${darken('rgba(255, 255, 255, 0.12)', 0.16)}`,
            borderRadius: '3px',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            borderRadius: '3px',
            margin: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: `${darken('rgba(255, 255, 255, 0.12)', 0.16)}`,
            borderRadius: '3px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        // Name of the slot
        root: {
          transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0,
          color: '#fff',
          backgroundImage: 'none',
          backgroundColor: 'rgb(30, 33, 37)',
          boxShadow:
            '0 0 0 0 rgb(0, 0, 0), 0 0 0 0 rgb(0, 0, 0), 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            boxShadow: 'none',
          },
          boxShadow: 'none',
        },
        textPrimary: {
          color: '#fff',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, .4)',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: 'rgb(255,255,255)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&.MuiNativeSelect-root': {
            display: 'inline-flex',
            flexDirection: 'column',
            position: 'relative',
            minWidth: 0,
            padding: 0,
            margin: 0,
            verticalAlign: 'top',
            marginTop: '8px',
            marginBottom: '4px',
            border: '1px solid rgba(255,255,255,.12)',
            borderRadius: '4px',
            width: '100%',
            '& select': {
              font: 'inherit',
              letterSpacing: 'inherit',
              color: 'currentColor',
              border: 0,
              boxSizing: 'content-box',
              background: 'none',
              height: '1.4375em',
              margin: 0,
              // '-webkit-tap-highlight-color': 'transparent',
              display: 'block',
              minWidth: 0,
              width: '100%',
              animationName: 'mui-auto-fill-cancel',
              animationDuration: '10ms',
              padding: '8.5px 14px',
              lineHeight: 1.75,
            },
            '&:before': {
              border: 'none',
            },
          },
        },
      },
    },
    MuiNativeSelect: {
      styleOverrides: {
        root: {
          border: '1px solid red',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: skyBlue[100],
            },
          },
        },
        input: {
          // padding: '12.5px',
          lineHeight: 1.75,
          '&::-webkit-calendar-picker-indicator': {
            filter: 'invert(1)',
          },
          '&.MuiSelect-select': {
            paddingTop: '10px',
            paddingBottom: '10px',
          },
        },
        notchedOutline: {
          borderColor: 'rgba(255,255,255,.12)',
          '& fieldset': {
            color: 'violet',
          },
        },
      },
    },
  },
};

export const getTheme = (mode) => {
  return responsiveFontSizes(createTheme(mode ? dark : light));
};
